import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { useApi } from '~shared/providers/use-api';

import {
  keepPreviousData,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { SummaryApi, SummaryTableParameters } from '../api';
import { SUMMARY_API_KEY } from '../api';
import type { SummaryFilter, GroupByField, SummaryTableData } from '../summary';

export const useGetSummaryGraphQuery = (filter?: SummaryFilter) => {
  const summaryApi = useApi<SummaryApi>(SUMMARY_API_KEY);

  return useQuery({
    queryKey: ['get-graph', filter],
    queryFn: async () =>
      summaryApi.getGraphData({ filter: filter as SummaryFilter }),
    placeholderData: keepPreviousData,
    enabled: !!filter,
  });
};

export const useGetSummaryTableQuery = (
  type: GroupByField,
  requestOptions: SummaryTableParameters,
  options: Omit<
    UseQueryOptions<{
      items: SummaryTableData[];
      total: number;
    }>,
    'queryKey' | 'queryFn'
  > = {}
) => {
  const summaryApi = useApi<SummaryApi>(SUMMARY_API_KEY);

  return useQuery({
    ...options,
    queryKey: ['find-summary', requestOptions, type],
    queryFn: async () => {
      const { items, pageInfo } = await (type === 'by-merchant'
        ? summaryApi.getTableDataByMerchant(nonNullableValue(requestOptions))
        : summaryApi.getTableDataByPos(nonNullableValue(requestOptions)));

      return {
        items,
        total: pageInfo.total,
      };
    },
    placeholderData: keepPreviousData,
  });
};
